import React, { useMemo, useState } from 'react';
import { Box, Chip, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { AutomationBand, RecentTicketAiSummary } from '../apis/mspAnalyticsApi';
import { NEUTRAL } from '../styles/brandTokens';
import { visibleFitBands as automationBands } from './RecentTicketAutomationPanel';
import { NUMERIC_COL_WIDTH, headCellSx, tableSx, SortableHead, MetricButton } from './ticketAnalysisTableParts';
import { BandCounts, CategorySort, CategorySortKey, DEFAULT_CATEGORY_SORT, nextCategorySort, sortCategoryRows } from './ticketCategorySort';

type CategoryRow = RecentTicketAiSummary['theo_categories'][number];
type SubcategoryRow = NonNullable<CategoryRow['details']>[number];

interface TicketCategoriesTableProps {
  rows: CategoryRow[];
  bandCounts: BandCounts;
  hasAutomation: boolean;
  showSkeleton: boolean;
  disabled: boolean;
  selectedCategory?: string;
  onCategoryDrill: (row: CategoryRow) => void;
  onSubcategoryDrill: (row: CategoryRow, detail: SubcategoryRow) => void;
  onBandDrill: (band: AutomationBand, row: CategoryRow) => void;
}

const COLUMN_COUNT = 2 + automationBands.length;

export default function TicketCategoriesTable({ rows, bandCounts, hasAutomation, showSkeleton, disabled, selectedCategory, onCategoryDrill, onSubcategoryDrill, onBandDrill }: TicketCategoriesTableProps) {
  const [sort, setSort] = useState<CategorySort>(DEFAULT_CATEGORY_SORT);
  const sortedRows = useMemo(() => sortCategoryRows(rows, bandCounts, sort), [rows, bandCounts, sort]);
  const handleSort = (key: CategorySortKey) => setSort(current => nextCategorySort(current, key));

  return (
    <TableContainer sx={tableSx}>
      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 760 }}>
        {/* Fixed layout reads widths from here, not from the grouped header row. */}
        <colgroup>
          <col />
          {Array.from({ length: COLUMN_COUNT - 1 }, (_, i) => <col key={i} style={{ width: NUMERIC_COL_WIDTH }} />)}
        </colgroup>
        <TableHead>
          <TableRow>
            <SortableHead rowSpan={2} label="Category" sortKey="label" sort={sort} onSort={handleSort} align="left">Category / subcategory</SortableHead>
            <SortableHead rowSpan={2} label="Volume" sortKey="count" sort={sort} onSort={handleSort}>Volume</SortableHead>
            <TableCell colSpan={automationBands.length} align="center" sx={{ ...headCellSx, pb: 0.5, borderBottom: 0 }}>
              <Box sx={{ borderBottom: `1px solid ${NEUTRAL.borderStrong}`, pb: 0.5, mx: 1 }}>Fit</Box>
            </TableCell>
          </TableRow>
          <TableRow>
            {automationBands.map(band => (
              <SortableHead key={band.key} label={`${band.label} fit`} sortKey={band.key} sort={sort} onSort={handleSort} disabled={!hasAutomation}>
                <Stack direction="row" alignItems="center" gap={0.75} component="span">
                  <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: band.color, flexShrink: 0 }} />
                  {band.key === 'insufficient' ? 'Insufficient' : band.label}
                </Stack>
              </SortableHead>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={{ '@keyframes categoriesAppear': { from: { opacity: 0 }, to: { opacity: 1 } }, animation: 'categoriesAppear 200ms ease-out', '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}>
          {sortedRows.map(row => (
            <TableRow key={row.key} selected={selectedCategory === row.key}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600, color: NEUTRAL.text, mb: 1 }}>{row.label}</Typography>
                <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75}>
                  {row.details?.map(detail => (
                    <Chip key={detail.key} size="small" component="button" clickable disabled={disabled || !detail.count}
                      label={`${detail.label}: ${detail.count}`} onClick={() => onSubcategoryDrill(row, detail)}
                      sx={{ bgcolor: NEUTRAL.hover, color: NEUTRAL.textSecondary, fontWeight: 500, height: 24 }} />
                  ))}
                </Stack>
              </TableCell>
              <TableCell align="right">
                <MetricButton bar count={row.count} pct={row.share_pct} disabled={disabled}
                  ariaLabel={`${row.count} tickets · ${row.share_pct}%`} onClick={() => onCategoryDrill(row)} />
              </TableCell>
              {automationBands.map(band => {
                const count = bandCounts[row.key]?.[band.key] || 0;
                const pct = row.count ? count / row.count * 100 : 0;
                return (
                  <TableCell align="right" key={band.key}>
                    {hasAutomation
                      ? <MetricButton count={count} pct={pct} disabled={!count || disabled}
                          ariaLabel={`${count} tickets · ${pct.toFixed(1)}%`} onClick={() => onBandDrill(band.key, row)} />
                      : <Typography variant="body2" color="text.disabled" sx={{ px: 1 }}>—</Typography>}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          {showSkeleton && Array.from({ length: 3 }, (_, i) => (
            <TableRow key={`skeleton-${i}`}><TableCell colSpan={COLUMN_COUNT}><Skeleton /></TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
